import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_CONFIG from '../config/api.js';

const CategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    englishName: '',
    description: '',
    order: 0,
    isActive: true
  });

  const [loading, setLoading] = useState(isEditMode);

  const fetchCategory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_CONFIG.BASE_URL}/categories/${id}`);
      const category = res.data.data;
      
      setFormData({
        name: category.name || '',
        englishName: category.englishName || '',
        description: category.description || '',
        order: category.order || 0,
        isActive: category.isActive !== undefined ? category.isActive : true
      });
      
      setLoading(false);
    } catch (err) {
      toast.error('Failed to fetch category details');
      navigate('/categories');
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchCategory();
    }
  }, [isEditMode, fetchCategory]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      };
      
      if (isEditMode) {
        await axios.put(
          `${API_CONFIG.BASE_URL}/categories/${id}`,
          formData,
          config
        );
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API_CONFIG.BASE_URL}/categories`, formData, config);
        toast.success('Category created successfully');
      }
      
      navigate('/categories');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save category');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">
        {isEditMode ? 'Edit Category' : 'Add Category'}
      </h1>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="name" className="form-label">
                Name (नेपाली) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label htmlFor="englishName" className="form-label">
                English Name
              </label>
              <input
                type="text"
                id="englishName"
                name="englishName"
                value={formData.englishName}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="col-span-2">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div>
              <label htmlFor="order" className="form-label">
                Display Order
              </label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/categories')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update' : 'Create'} Category
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm; 